import React, { useState, lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import includes from 'lodash/includes';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { Modal, ModalBody, Button } from 'reactstrap';
import { FormattedMessage } from 'react-intl';

import store from 'Redux/store';
import { toggleSelectableFreeProductsModal } from 'Redux/ducks/modal';
import { addVoucher } from 'Redux/ducks/voucher';
import { currencyCode } from 'Helpers/Geo/data';
import Price from 'Components/Utility/Price/Price';
import useViewport from 'Hooks/useViewport';

const MysteryReveal = lazy(() => import(/* webpackChunkName: "MysteryReveal" */ 'Components/Order/KeyReveals/MysteryReveal'));
const TieredUpsellModal = lazy(() => import(/* webpackChunkName: "TieredUpsellModal" */ 'Components/OnSale/TieredUpsellModal'));

const StripBanner = ({ banner }) => {
  const [open, setOpen] = useState(false);

  const { isBelowMd } = useViewport();

  let customTheme = false;
  if (get(banner, 'promotional_message_data.custom_theme', false)) {
    customTheme = true;
  }

  const stripBannerDetails = get(banner, 'promotional_message_data', {});
  const promotionType = get(stripBannerDetails, 'modal_type', '');
  const stripBannerUrl = get(stripBannerDetails, 'url', null);
  let splitUrl = '';
  let LinkType = 'none';
  let voucherCode = '';

  if (stripBannerUrl) {
    splitUrl = stripBannerUrl.split('en/');
    if (banner.external_link) {
      LinkType = 'external_link';
    }
    if (!banner.external_link && splitUrl[0] === 'https://www.fanatical.com/') {
      LinkType = 'internal_link';
    }
    if (!banner.external_link && splitUrl[1] && includes(splitUrl[1], '?add_voucher=')) {
      LinkType = 'add_voucher';
      const splitUrlVoucher = stripBannerUrl.split('?add_voucher=');
      if (splitUrlVoucher[1]) voucherCode = splitUrlVoucher[1];
    }
  }
  if (banner.free_game_choice) {
    LinkType = 'free_game_choice';
  }
  if (promotionType === 'no_modal') {
    LinkType = 'none';
  }
  if (promotionType && promotionType.toLowerCase() !== 'default') {
    LinkType = 'modal';
  }
  if (banner._id === '660e945a41ac76a63711c9c1' || banner._id === '660e946141ac76a63711c9ca') { // LIVE HOMEPAGE | LIVE ON SALE PAGE
    LinkType = 'tiered_upsell_modal';
  }

  const handleModel = () => {
    setOpen(!open);
  };

  let minSpend = [];

  if (banner.product_price) {
    minSpend = {
      USD: banner.product_price.USD,
      GBP: banner.product_price.GBP,
      EUR: banner.product_price.EUR,
      CAD: banner.product_price.CAD,
      AUD: banner.product_price.AUD,
      RUB: banner.product_price.RUB,
      JPY: banner.product_price.JPY,
    };
  }

  function stripBannerContent() {
    let primaryMessage = get(banner, 'primary_message', '');
    let secondaryMessage = get(banner, 'secondary_message', '');
    const primaryMessageMobile = get(banner, 'primary_message_mobile', '');
    const secondaryMessageMobile = get(banner, 'secondary_message_mobile', '');
    const showPrice = get(banner, 'show_price', false);

    let showSecondaryMessage = showPrice && secondaryMessage;

    if (isBelowMd && primaryMessageMobile) {
      primaryMessage = primaryMessageMobile;
    }

    if (isBelowMd && secondaryMessageMobile) {
      secondaryMessage = secondaryMessageMobile;
    }

    if (isBelowMd && primaryMessageMobile && !secondaryMessageMobile) {
      showSecondaryMessage = false;
    }

    return (
      <div className="container">
        {primaryMessage}
        {showPrice && <span>&nbsp;<Price price={banner.product_price} /></span>}
        {showSecondaryMessage &&
          <React.Fragment>
            &nbsp;{secondaryMessage}
          </React.Fragment>
        }
        {LinkType === 'free_game_choice' &&
          <React.Fragment>
            &nbsp;
            <span className="strip-banner-underlined-text">
              <FormattedMessage id="strip-banner.read-more" defaultMessage="Read more" />
            </span>
          </React.Fragment>
        }
      </div>
    );
  }

  return (
    <React.Fragment>
      {LinkType === 'external_link' &&
        <a
          href={stripBannerUrl}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="strip-banner-under-takeover"
          style={{
            backgroundColor: customTheme ? banner.promotional_message_data.background_colour : '',
            color: customTheme ? banner.promotional_message_data.text_colour : '',
          }}
        >
          {stripBannerContent()}
        </a>
      }

      {LinkType === 'internal_link' &&
        <Link
          to={splitUrl[1]}
          className="strip-banner-under-takeover"
          style={{
            backgroundColor: customTheme ? banner.promotional_message_data.background_colour : '',
            color: customTheme ? banner.promotional_message_data.text_colour : '',
          }}
        >
          {stripBannerContent()}
        </Link>
      }

      {LinkType === 'modal' &&
        <React.Fragment>
          <button
            className="strip-banner-under-takeover"
            style={{
              backgroundColor: customTheme ? banner.promotional_message_data.background_colour : '',
              color: customTheme ? banner.promotional_message_data.text_colour : '',
            }}
            onClick={() => handleModel()}
          >
            {stripBannerContent()}
          </button>
          { /* TODO Switch this to use LazyModal */}
          <Modal
            centered
            isOpen={open}
            toggle={() => setOpen(false)}
            className="mystery-test-modal"
          >
            <Suspense>
              <Button color="link" className="close-button" onClick={() => setOpen(false)}>
                <FontAwesomeIcon icon={faXmark} size="lg" title="Close modal" />
              </Button>
              <ModalBody>
                <MysteryReveal
                  testReveal
                  testRevealType={promotionType}
                />
              </ModalBody>
            </Suspense>
          </Modal>
        </React.Fragment>
      }

      {LinkType === 'tiered_upsell_modal' &&
        <React.Fragment>
          <button
            className="strip-banner-under-takeover"
            style={{
              backgroundColor: customTheme ? banner.promotional_message_data.background_colour : '',
              color: customTheme ? banner.promotional_message_data.text_colour : '',
            }}
            onClick={() => handleModel()}
          >
            {stripBannerContent()}
          </button>
          <Modal
            centered
            isOpen={open}
            toggle={() => setOpen(false)}
            className="mystery-test-modal"
          >
            <Button color="link" className="close-button" onClick={() => setOpen(false)}>
              <FontAwesomeIcon icon={faXmark} size="lg" title="Close modal" />
            </Button>
            <ModalBody>
              <TieredUpsellModal closeModal={() => setOpen(false)} />
            </ModalBody>
          </Modal>
        </React.Fragment>
      }

      {LinkType === 'free_game_choice' &&
        <React.Fragment>
          <button
            className="strip-banner-under-takeover"
            style={{
              backgroundColor: customTheme ? banner.promotional_message_data.background_colour : '',
              color: customTheme ? banner.promotional_message_data.text_colour : '',
            }}
            onClick={() => store.dispatch(toggleSelectableFreeProductsModal({
              minSpend: minSpend[currencyCode],
              promotionType,
            }))}
          >
            {stripBannerContent()}
          </button>
        </React.Fragment>
      }

      {LinkType === 'add_voucher' &&
        <button
          className="strip-banner-under-takeover"
          style={{
            backgroundColor: customTheme ? banner.promotional_message_data.background_colour : '',
            color: customTheme ? banner.promotional_message_data.text_colour : '',
          }}
          onClick={() => {
            store.dispatch(addVoucher(voucherCode, null, true));
          }}
        >
          {stripBannerContent()}
        </button>
      }

      {LinkType === 'none' &&
        <div
          className="strip-banner-under-takeover"
          style={{
            backgroundColor: customTheme ? banner.promotional_message_data.background_colour : '',
            color: customTheme ? banner.promotional_message_data.text_colour : '',
          }}
        >
          {stripBannerContent()}
        </div>
      }
    </React.Fragment>
  );
};

StripBanner.propTypes = {
  banner: PropTypes.shape({}),
};

StripBanner.defaultProps = {
  banner: {},
};

export default StripBanner;
